import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import {
  CheckCircle,
  Pets,
  CalendarToday,
  Scale,
  LocationOn,
  Female,
  Male,
  Cake,
  Info,
} from "@mui/icons-material";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface WeaningData {
  message: string;
  content_type: string;
  status: string;
  name: string;
  breed_code: string;
  gender: string;
  birth_date: string;
  weight_kg: number;
  weaning_date: string;
  handling_date: string;
  missing_fields: string[];
  cattle_tag: string;
  operation_code: string;
  summary: string;
  age_months: number;
  paddock: string;
}

export interface WeaningFeedbackData {
  data: WeaningData;
  resumo: string;
  url: string;
}

interface WeaningFeedbackViewProps {
  weaningData: WeaningFeedbackData;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getGenderIcon = (gender: string) => {
  return gender.toLowerCase() === 'fêmea' ? <Female /> : <Male />;
};

const getGenderColor = (gender: string) => {
  return gender.toLowerCase() === 'fêmea' 
    ? DESIGN_TOKENS.colors.status.warning[500] 
    : DESIGN_TOKENS.colors.brand.primary;
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completo':
      return DESIGN_TOKENS.colors.status.success[500];
    case 'pendente':
      return DESIGN_TOKENS.colors.status.warning[500];
    case 'erro':
      return DESIGN_TOKENS.colors.status.error[500];
    default:
      return DESIGN_TOKENS.colors.neutral[500];
  }
};

export const WeaningFeedbackView: React.FC<WeaningFeedbackViewProps> = ({
  weaningData,
}) => {
  const { data } = weaningData;

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 60px)", overflow: "auto" }}>
      {/* Header com Status da Operação */}
      <Card sx={{ mb: 3, border: `2px solid ${getStatusColor(data.status)}` }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: getStatusColor(data.status),
                width: 56,
                height: 56,
              }}
            >
              <CheckCircle sx={{ fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: DESIGN_TOKENS.colors.text.primary,
                  mb: 0.5,
                }}
              >
                Desmama Registrada
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              >
                {data.message}
              </Typography>
            </Box>
            <Chip
              label={data.status}
              sx={{
                bgcolor: getStatusColor(data.status),
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Resumo da Operação */}
          <Alert 
            severity="success" 
            icon={<Info />}
            sx={{ mt: 2 }}
          >
            <Typography variant="body2">
              {data.summary}
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Informações do Animal */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: DESIGN_TOKENS.colors.text.primary,
              fontWeight: 600,
            }}
          >
            <Pets color="primary" />
            Informações do Animal
          </Typography>

          <Box sx={{ display: "grid", gap: 2 }}>
            {/* Nome e Identificação */}
            <Paper sx={{ p: 2, bgcolor: DESIGN_TOKENS.colors.neutral[50] }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: getGenderColor(data.gender),
                      width: 40,
                      height: 40,
                    }}
                  >
                    {getGenderIcon(data.gender)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {data.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Brinco: {data.cattle_tag}
                    </Typography>
                  </Box>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Raça
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {data.breed_code}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Gênero
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {data.gender}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Detalhes da Desmama */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 2 }}>
              {/* Data de Nascimento e Idade */}
              <Paper sx={{ p: 2, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Cake color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Nascimento e Idade
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Nascimento
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {formatDate(data.birth_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Idade na Desmama
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.age_months} meses
                </Typography>
              </Paper>

              {/* Data da Desmama */}
              <Paper sx={{ p: 2, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CalendarToday color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Data da Desmama
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Desmama
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {formatDate(data.weaning_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data de Registro
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(data.handling_date)}
                </Typography>
              </Paper>

              {/* Peso e Localização */}
              <Paper sx={{ p: 2, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Scale color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Peso Atual
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Peso
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.weight_kg} kg
                </Typography>
              </Paper>

              {/* Localização */}
              <Paper sx={{ p: 2, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <LocationOn color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Localização
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Pasto
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.paddock}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Informações Adicionais */}
      {data.missing_fields && data.missing_fields.length > 0 && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: DESIGN_TOKENS.colors.text.primary,
                fontWeight: 600,
              }}
            >
              Campos Pendentes
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                Os seguintes campos ainda precisam ser preenchidos: {data.missing_fields.join(", ")}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WeaningFeedbackView;